import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faPage, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteAPIKey, getAPIKeys } from 'api/apiKeys';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { EmptyData } from 'components/util';
import { DOCS_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { apiQueryKeys } from 'queries/api-query-keys';
import { queryClient } from 'queries/queryClient';
import { APIKey } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<APIKey>();

const APIKeySettings = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination } = Table.useReactTableState();
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: apiQueryKeys.apiKeysFiltered(accountId, params),
        queryFn: () => getAPIKeys(accountId, params),
    });

    const { mutate: deleteAPIKeyMutation, isPending: isDeletingAPIKey } = useMutation({
        mutationFn: (apiKeyId: number) => deleteAPIKey(accountId, apiKeyId),
        onSuccess: () => {
            toast.success('Deleted API key successfully');
            queryClient.invalidateQueries({
                queryKey: apiQueryKeys.apiKeys(accountId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete API key');
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Name',
                cell: () => <Table.CellDataOrEmpty />,
            }),
            columnHelper.accessor('created_at', {
                header: 'Created at',
                cell: info => {
                    const key = info.row.original;
                    return dateFromServer(key.created_at).toLocaleString(
                        DateTime.DATETIME_SHORT_WITH_SECONDS,
                    );
                },
            }),
            columnHelper.accessor('last_used_at', {
                header: 'Last used',
                cell: info => {
                    const key = info.row.original;
                    return key.last_used_at ? (
                        dateFromServer(key.last_used_at).toRelative()
                    ) : (
                        <EmptyData />
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const key = info.row.original;
                    return (
                        <DropdownMenu.Root key={key.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.LinkItem
                                    to={`/accounts/${accountId}/settings/integrations/api/${key.id}/edit`}
                                >
                                    Edit
                                </DropdownMenu.LinkItem>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="text-red-500"
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete API key
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    This API key will immediately become unusable.
                                                    You will not be able to undo this action.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => deleteAPIKeyMutation(key.id)}
                                                    disabled={isDeletingAPIKey}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [deleteAPIKeyMutation, isDeletingAPIKey, accountId],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, data);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <span className="text-2xl font-medium">API Keys</span>
                <div className="flex gap-4">
                    <Button
                        size="sm"
                        variant={'outline'}
                        color={'neutral'}
                        onClick={() => window.open(DOCS_LINK, '_blank')}
                    >
                        <FontAwesomeIcon icon={faPage} />
                        Read docs
                    </Button>
                    <Link to={`/accounts/${accountId}/settings/integrations/api/new`}>
                        <Button variant="solid" size="sm">
                            <FontAwesomeIcon icon={faPlus} /> New API key
                        </Button>
                    </Link>
                </div>
            </div>
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <div className={'flex flex-col gap-4'}>
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon />
                            <Table.EmptyTitle>No api keys found</Table.EmptyTitle>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

export { APIKeySettings };
